/**
 * feedBoardSetup.ts — Provisioning for the Task Feed and Data Sources boards.
 *
 * These boards are NOT created by the standard boardSetup.ts because they
 * already exist in Chanelle's workspace:
 *   - Task Feed:       5092855258
 *   - Data Sources:    5092854138
 *   - Aggregator:      5092855497
 *
 * This module provides:
 *   1. Column schema definitions for reference
 *   2. A function to ensure the expected columns exist
 *   3. A function to populate the Data Sources registry
 */

import { mutateCreateColumn, mutateCreateItem } from '../api/mutations';
import { queryBoardItems, columnMap } from '../api/queries';
import {
  TASK_FEED_BOARD_ID,
  DATA_SOURCES_BOARD_ID,
} from '../intelligence/feedAggregator';

// ─── Task Feed Board Schema ─────────────────────────────────────────────────

/**
 * Expected columns on the Task Feed board (ID: 5092855258).
 *
 * If any column is missing the aggregator will silently skip writing to it,
 * but the feed will have gaps.  Use `ensureTaskFeedColumns()` to create
 * any missing columns.
 */
export const TASK_FEED_COLUMNS = [
  { title: 'Record Type',       type: 'status'    },
  { title: 'Source Board ID',   type: 'text'      },
  { title: 'Source Board Name', type: 'text'      },
  { title: 'Source Item ID',    type: 'text'      },
  { title: 'Source Workspace',  type: 'text'      },
  { title: 'Assignee User ID', type: 'text'      },
  { title: 'Status',            type: 'status'    },
  { title: 'Priority',          type: 'status'    },
  { title: 'Due Date',          type: 'date'      },
  { title: 'Mention Source',    type: 'text'      },
  { title: 'Mention Text',      type: 'long_text' },
  { title: 'Dedup Key',         type: 'text'      },
  { title: 'Score',             type: 'numbers'   },
] as const;

// ─── Data Sources Board Schema ───────────────────────────────────────────────

/**
 * Expected columns on the Data Sources board (ID: 5092854138).
 */
export const DATA_SOURCES_COLUMNS = [
  { title: 'Board ID',          type: 'text'     },
  { title: 'Workspace',         type: 'text'     },
  { title: 'Include Tasks',     type: 'checkbox' },
  { title: 'Include Mentions',  type: 'checkbox' },
  { title: 'Last Sync',         type: 'date'     },
] as const;

// ─── Column Provisioning ─────────────────────────────────────────────────────

/**
 * Ensure the Task Feed board has all expected columns.
 * Reads existing columns and creates any that are missing.
 */
export async function ensureTaskFeedColumns(): Promise<{
  created: string[];
  existing: string[];
}> {
  const created: string[] = [];
  const existing: string[] = [];

  // Read current items to discover existing columns
  const items = await queryBoardItems(TASK_FEED_BOARD_ID, 1);
  const existingCols = new Set<string>();

  if (items.length > 0) {
    for (const cv of items[0].column_values ?? []) {
      existingCols.add(cv.column.title.toLowerCase());
    }
  }

  for (const col of TASK_FEED_COLUMNS) {
    if (existingCols.has(col.title.toLowerCase())) {
      existing.push(col.title);
    } else {
      try {
        await mutateCreateColumn(TASK_FEED_BOARD_ID, col.title, col.type);
        created.push(col.title);
      } catch (err) {
        console.warn(`[FeedSetup] Failed to create column "${col.title}":`, err);
      }
    }
  }

  return { created, existing };
}

/**
 * Ensure the Data Sources board has all expected columns.
 */
export async function ensureDataSourcesColumns(): Promise<{
  created: string[];
  existing: string[];
}> {
  const created: string[] = [];
  const existing: string[] = [];

  const items = await queryBoardItems(DATA_SOURCES_BOARD_ID, 1);
  const existingCols = new Set<string>();

  if (items.length > 0) {
    for (const cv of items[0].column_values ?? []) {
      existingCols.add(cv.column.title.toLowerCase());
    }
  }

  for (const col of DATA_SOURCES_COLUMNS) {
    if (existingCols.has(col.title.toLowerCase())) {
      existing.push(col.title);
    } else {
      try {
        await mutateCreateColumn(DATA_SOURCES_BOARD_ID, col.title, col.type);
        created.push(col.title);
      } catch (err) {
        console.warn(`[FeedSetup] Failed to create column "${col.title}":`, err);
      }
    }
  }

  return { created, existing };
}

// ─── Data Source Registration ────────────────────────────────────────────────

/**
 * Register a board in the Data Sources registry.
 * Checks for existing entries to avoid duplicates.
 */
export async function registerDataSource(
  boardName: string,
  boardId: string,
  workspace = '',
  includeTasks = true,
  _includeMentions = true,
): Promise<string | null> {
  // Check if already registered
  const items = await queryBoardItems(DATA_SOURCES_BOARD_ID, 200);
  for (const item of items) {
    const cv = columnMap(item);
    if (cv['board_id'] === boardId || cv['text'] === boardId) {
      console.log(`[FeedSetup] Board ${boardId} already registered`);
      return item.id;
    }
  }

  try {
    const columnValues: Record<string, unknown> = {
      text: boardId,
    };
    if (workspace) columnValues['text0'] = workspace;
    if (includeTasks) columnValues['checkbox'] = { checked: 'true' };

    const item = await mutateCreateItem({
      boardId: DATA_SOURCES_BOARD_ID,
      itemName: boardName,
      columnValues,
    });

    console.log(`[FeedSetup] Registered data source: ${boardName} (${boardId})`);
    return item.id;
  } catch (err) {
    console.warn(`[FeedSetup] Failed to register data source ${boardName}:`, err);
    return null;
  }
}

/**
 * Register all known Chanelle OS boards as data sources.
 * This seeds the Data Sources board with the boards from boardIds.
 */
export async function registerAllKnownSources(
  boardIds: Record<string, string | undefined>,
): Promise<void> {
  const entries = [
    { name: 'COS — Actions',       key: 'actions',       tasks: true,  mentions: true  },
    { name: 'COS — Topics',        key: 'topics',        tasks: false, mentions: true  },
    { name: 'COS — Notes',         key: 'notes',         tasks: false, mentions: true  },
    { name: 'COS — Meetings',      key: 'meetings',      tasks: false, mentions: true  },
    { name: 'COS — Board Monitor', key: 'board_monitor',  tasks: true,  mentions: false },
  ];

  for (const entry of entries) {
    const boardId = boardIds[entry.key];
    if (boardId) {
      await registerDataSource(
        entry.name,
        boardId,
        'Chanelle OS',
        entry.tasks,
        entry.mentions,
      );
    }
  }
}
